/** changedoc.ts — 결성계획서 대비 변경내역 생성·버전 관리 + 변경입력 파싱 */
import { genChangeDoc, ChangeInput, GenDocResult } from './api';
import type { Project, DocVer, DocVerMap, Master } from './types';

export const CHANGE_KEY = 'change';
const TITLE = '결성계획서 대비 변경내역';

function org(p: Project): string { return (p.master && p.master.org) ? p.master.org : (p.org || '조합'); }
export function changeFileName(p: Project, ext: string): string {
  return TITLE + '_' + (String(org(p)).replace(/[\\/:*?<>|]/g, ' ').trim() || '조합') + '.' + ext;
}
export function changeAutoV1(p: Project): DocVer {
  return { label: '자동 생성 v1', doc: changeFileName(p, 'docx'), pdf: changeFileName(p, 'pdf'), kind: 'auto' };
}
export function getChangeVers(p: Project): DocVer[] {
  const dv = p.docver && p.docver[CHANGE_KEY];
  if (dv && dv.length) { if (dv.length === 1 && dv[0].kind === 'auto' && !dv[0].s3doc && !dv[0].s3pdf) return [changeAutoV1(p)]; return dv; }
  return [changeAutoV1(p)];
}

// ── 변경입력(스왑행) 저장 구조 ──
export interface ChangeSwap { outName: string; outShare: string; inName: string; inShare: string; }
export interface ChangeData { date: string; swaps: ChangeSwap[]; }
export function emptySwap(): ChangeSwap { return { outName: '', outShare: '', inName: '', inShare: '' }; }
export function parseChange(raw: string): ChangeData {
  try {
    const o = JSON.parse(raw || '{}');
    return { date: o.date || '', swaps: Array.isArray(o.swaps) && o.swaps.length ? o.swaps : [emptySwap()] };
  } catch (e) { return { date: '', swaps: [emptySwap()] }; }
}
export function hasChange(d: ChangeData): boolean {
  return d.swaps.some((s) => (s.outName || '').trim() || (s.inName || '').trim());
}
export function toChangeInput(d: ChangeData): ChangeInput {
  const outs = d.swaps.filter((s) => (s.outName || '').trim()).map((s) => ({ name: s.outName.trim(), share: (s.outShare || '').trim() }));
  const ins = d.swaps.filter((s) => (s.inName || '').trim()).map((s) => ({ name: s.inName.trim(), share: (s.inShare || '').trim() }));
  return { date: d.date, outs, ins };
}

function applyChange(dv: DocVerMap, r: GenDocResult, p: Project): DocVer {
  const list = dv[CHANGE_KEY] && dv[CHANGE_KEY].length ? dv[CHANGE_KEY] : [changeAutoV1(p)];
  const onlyFake = list.length === 1 && list[0].kind === 'auto' && !list[0].s3doc;
  const ver: DocVer = {
    label: onlyFake ? '자동 생성 v1' : '재생성 v' + (list.length + 1),
    doc: r.doc, pdf: r.pdf, kind: 'auto', s3doc: r.s3doc, s3pdf: r.s3pdf, at: new Date().toISOString(),
  };
  dv[CHANGE_KEY] = onlyFake ? [ver] : [...list, ver];
  return ver;
}

/** 변경입력으로 실생성 → docver['change'] 패치 */
export async function regenChange(
  p: Project, input: ChangeInput,
  onPatch: (patch: Partial<Project>) => void, toast: (m: string) => void,
): Promise<DocVer> {
  const master = p.master as Master | undefined;
  if (!master) { toast('공통정보(마스터)가 없습니다 — 먼저 입력하세요'); throw new Error('no master'); }
  toast('변경내역 생성 중 · 잠시만요(2~4초)');
  const { results } = await genChangeDoc(p.id, master, input);
  const dv = JSON.parse(JSON.stringify(p.docver || {})) as DocVerMap;
  const ver = applyChange(dv, results[0], p);
  onPatch({ docver: dv });
  toast(results[0].converted ? '변경내역 생성 완료 · Word·PDF 저장됨' : '변경내역 생성 · ⚠ PDF 변환 실패');
  return ver;
}
